import { useState } from "react"

import DialogProvider from "@components/dialog_provider"
import { useCustomerPageContext } from "@contexts/customer"
import { removeCouponCodeFromCustomerMutation } from "@domain/customer/infos"
import { Stack, Typography } from "@mui/material"
import { useQueryClient } from "@tanstack/react-query"
import { showErrorToast } from "@utils/toast"

export default function RemoveCouponCodeDialog({
  isOpen,
  setClose
}: {
  isOpen: boolean
  setClose: () => void
}) {
  const { translate, currentCustomer } = useCustomerPageContext()
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)

  function handleClose() {
    if (loading) {
      return
    }

    setClose()
  }

  async function onSubmit() {
    const customerId = currentCustomer?._id
    const couponCodeId = currentCustomer?.package?.couponCode?._id

    if (!customerId || !couponCodeId) {
      return
    }

    setLoading(true)
    const { isSuccess, errorMessage } =
      await removeCouponCodeFromCustomerMutation({
        customerId,
        id: couponCodeId
      })

    if (!isSuccess) {
      showErrorToast(String(errorMessage))
      setLoading(false)
      return
    }

    queryClient.invalidateQueries({
      queryKey: ["customer-by-id", customerId]
    })
    setLoading(false)
    handleClose()
  }

  return (
    <DialogProvider
      title={translate("remove_coupon_code")}
      leftButton={{
        text: translate("cancel"),
        color: "secondary",
        onClick: handleClose,
        disabled: loading
      }}
      rightButton={{
        text: translate("delete"),
        color: "error",
        onClick: onSubmit,
        loading,
        disabled: !currentCustomer?.package?.couponCode?._id
      }}
      isOpen={isOpen}
      setClose={handleClose}
      size="small"
    >
      <Stack justifyContent={"center"} alignItems={"center"} flex={1}>
        <Typography align="center">
          Are you sure you want to remove this coupon code from customer?
        </Typography>
      </Stack>
    </DialogProvider>
  )
}
